/**
 * Dashboard page — drag-and-drop priority queue.
 * Always-expanded job cards with prominent clock in/out.
 * Filter tabs: All, In Design, Ready to Build, Past Due, Waiting on Quotes, Ready to Ship.
 * Secondary page tabs: Active Queue | Shipped | Cancelled.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { differenceInDays, parseISO, format } from 'date-fns';

import SortableJobCard from '../components/dashboard/SortableJobCard';
import PurchasingTab from '../components/dashboard/PurchasingTab';
import JobCreateModal from '../components/jobs/JobCreateModal';
import DailyReportModal from '../components/agent/DailyReportModal';
import AIReasoningModal from '../components/scheduling/AIReasoningModal';
import { LoadingSpinner, EmptyState, SearchBar, Button } from '../components/common';
import { hasNewDailyReport } from '../api/agent';
import useJobQueue from '../hooks/useJobQueue';
import {
  clockIn, clockOut, getActiveClock,
  fetchReadyToShipJobs, fetchShippedJobs, fetchArchivedJobs,
  fetchCancelledJobs, backfillJobStatuses,
} from '../api/jobs';
import { fetchPendingParts } from '../api/purchasing';

// ---------------------------------------------------------------------------
// Filter definitions — computed dynamically from jobs array
// ---------------------------------------------------------------------------

function buildFilters(jobs) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const counts = {
    all: jobs.length,
    in_design: jobs.filter((j) => j.is_design_job).length,
    ready_to_build: jobs.filter((j) => j.status === 'ready_to_build').length,
    past_due: jobs.filter((j) => {
      if (!j.target_due_date) return false;
      return differenceInDays(parseISO(j.target_due_date), today) < 0 &&
        !['ready_to_ship', 'shipped', 'archived', 'cancelled'].includes(j.status);
    }).length,
    waiting_quotes: jobs.filter((j) =>
      j.status === 'waiting_on_quotes'
    ).length,
    quotes_received: jobs.filter((j) => j.status === 'quotes_received').length,
    flagged: jobs.filter((j) => j.is_flagged).length,
  };

  return [
    { key: 'all',            label: 'All',               count: counts.all,            accent: 'blue' },
    ...(counts.in_design > 0 ? [
      { key: 'in_design', label: '✏️ In Design', count: counts.in_design, accent: 'purple' },
    ] : []),
    { key: 'ready_to_build', label: '✓ Ready to Build',  count: counts.ready_to_build, accent: 'emerald' },
    ...(counts.past_due > 0 ? [
      { key: 'past_due', label: '⚠ Past Due', count: counts.past_due, accent: 'red' },
    ] : []),
    ...(counts.waiting_quotes > 0 ? [
      { key: 'waiting_quotes', label: 'Waiting on Quotes', count: counts.waiting_quotes, accent: 'amber' },
    ] : []),
    ...(counts.quotes_received > 0 ? [
      { key: 'quotes_received', label: 'Quotes Received', count: counts.quotes_received, accent: 'teal' },
    ] : []),
    ...(counts.flagged > 0 ? [
      { key: 'flagged', label: '⚑ BOM Changed', count: counts.flagged, accent: 'yellow' },
    ] : []),
  ];
}

// ---------------------------------------------------------------------------
// Apply active filter to jobs list
// ---------------------------------------------------------------------------

function applyFilter(jobs, filterKey, searchQuery) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let filtered = jobs;

  switch (filterKey) {
    case 'in_design':
      filtered = jobs.filter((j) => j.is_design_job);
      break;
    case 'ready_to_build':
      filtered = jobs.filter((j) => j.status === 'ready_to_build');
      break;
    case 'past_due':
      filtered = jobs.filter((j) => {
        if (!j.target_due_date) return false;
        return differenceInDays(parseISO(j.target_due_date), today) < 0 &&
          !['ready_to_ship', 'shipped', 'archived', 'cancelled'].includes(j.status);
      });
      break;
    case 'waiting_quotes':
      filtered = jobs.filter((j) =>
        j.status === 'waiting_on_quotes'
      );
      break;
    case 'quotes_received':
      filtered = jobs.filter((j) => j.status === 'quotes_received');
      break;
    case 'flagged':
      filtered = jobs.filter((j) => j.is_flagged);
      break;
    default:
      break;
  }

  if (searchQuery) {
    const q = searchQuery.toLowerCase();
    filtered = filtered.filter(
      (j) =>
        String(j.job_number).includes(q) ||
        (j.customer_name || '').toLowerCase().includes(q) ||
        (j.parent_item_no || '').toLowerCase().includes(q) ||
        (j.work_description || '').toLowerCase().includes(q)
    );
  }

  return filtered;
}

// ---------------------------------------------------------------------------
// Accent color classes per filter type
// ---------------------------------------------------------------------------

const ACCENT_ACTIVE = {
  blue:    'bg-blue-600 text-white',
  emerald: 'bg-emerald-600 text-white',
  red:     'bg-red-600 text-white',
  amber:   'bg-amber-500 text-white',
  green:   'bg-green-600 text-white',
  yellow:  'bg-yellow-500 text-white',
  teal:    'bg-teal-600 text-white',
  purple:  'bg-purple-600 text-white',
};

const ACCENT_INACTIVE = {
  blue:    'bg-gray-100 text-gray-600 hover:bg-gray-200',
  emerald: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
  red:     'bg-red-50 text-red-700 hover:bg-red-100',
  amber:   'bg-amber-50 text-amber-700 hover:bg-amber-100',
  green:   'bg-green-50 text-green-700 hover:bg-green-100',
  yellow:  'bg-yellow-50 text-yellow-700 hover:bg-yellow-100',
  teal:    'bg-teal-50 text-teal-700 hover:bg-teal-100',
  purple:  'bg-purple-50 text-purple-700 hover:bg-purple-100',
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function DashboardPage() {
  const { jobs, loading, error, refresh, handleReorder, handlePin, handleApplySuggested } = useJobQueue();
  const [pageTab, setPageTab] = useState('queue');
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeClock, setActiveClock] = useState(null);
  const [clockLoading, setClockLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [aiReasoningResult, setAiReasoningResult] = useState(null);
  const [aiModalOpen, setAiModalOpen] = useState(false);

  // AI Daily Report
  const [reportOpen, setReportOpen] = useState(false);
  const [reportAutoGen, setReportAutoGen] = useState(false);
  const [hasNewReport, setHasNewReport] = useState(false);

  // Check for unread AI report on mount and periodically
  useEffect(() => {
    hasNewDailyReport().then(setHasNewReport).catch(() => {});
    const iv = setInterval(() => {
      hasNewDailyReport().then(setHasNewReport).catch(() => {});
    }, 120000); // every 2 min
    return () => clearInterval(iv);
  }, []);

  // Secondary tab data
  const [readyToShipJobs, setReadyToShipJobs] = useState([]);
  const [readyToShipLoading, setReadyToShipLoading] = useState(false);
  const [shippedJobs, setShippedJobs] = useState([]);
  const [shippedLoading, setShippedLoading] = useState(false);
  const [archivedJobs, setArchivedJobs] = useState([]);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [cancelledJobs, setCancelledJobs] = useState([]);
  const [cancelledLoading, setCancelledLoading] = useState(false);
  const [purchasingBadge, setPurchasingBadge] = useState(0);

  async function loadReadyToShipJobs() {
    setReadyToShipLoading(true);
    try {
      const resp = await fetchReadyToShipJobs();
      setReadyToShipJobs(resp.data || resp.results || []);
    } catch { setReadyToShipJobs([]); }
    finally { setReadyToShipLoading(false); }
  }
  async function loadShippedJobs() {
    setShippedLoading(true);
    try {
      const resp = await fetchShippedJobs();
      setShippedJobs(resp.data || resp.results || []);
    } catch { setShippedJobs([]); }
    finally { setShippedLoading(false); }
  }
  async function loadArchivedJobs() {
    setArchivedLoading(true);
    try {
      const resp = await fetchArchivedJobs();
      setArchivedJobs(resp.data || resp.results || []);
    } catch { setArchivedJobs([]); }
    finally { setArchivedLoading(false); }
  }
  async function loadCancelledJobs() {
    setCancelledLoading(true);
    try {
      const resp = await fetchCancelledJobs();
      setCancelledJobs(resp.data || resp.results || []);
    } catch { setCancelledJobs([]); }
    finally { setCancelledLoading(false); }
  }

  async function loadPurchasingBadge() {
    try {
      const data = await fetchPendingParts();
      setPurchasingBadge(data?.badge_count || 0);
    } catch { setPurchasingBadge(0); }
  }

  useEffect(() => {
    loadReadyToShipJobs();
    loadShippedJobs();
    loadArchivedJobs();
    loadCancelledJobs();
    loadPurchasingBadge();
  }, []);
  useEffect(() => {
    if (pageTab === 'ready_to_ship') loadReadyToShipJobs();
    if (pageTab === 'shipped') loadShippedJobs();
    if (pageTab === 'archived') loadArchivedJobs();
    if (pageTab === 'cancelled') loadCancelledJobs();
  }, [pageTab]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Fetch active clock on mount
  useEffect(() => {
    async function fetchActiveClock() {
      try {
        const data = await getActiveClock();
        if (data) setActiveClock({ job: data.job, clock_in_at: data.clock_in_at, ...data });
      } catch { /* ignore */ }
    }
    fetchActiveClock();
  }, []);

  // Build filter tabs with live counts
  const filterTabs = useMemo(() => buildFilters(jobs), [jobs]);
  const filteredJobs = useMemo(
    () => applyFilter(jobs, activeFilter, searchQuery),
    [jobs, activeFilter, searchQuery],
  );

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = jobs.findIndex((j) => j.id === active.id);
      const newIndex = jobs.findIndex((j) => j.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return;

      const reordered = [...jobs];
      const [moved] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, moved);
      handleReorder(reordered);
    },
    [jobs, handleReorder],
  );

  const handleClockToggle = useCallback(
    async (job, laborType = 'standard', customStartTime = null) => {
      if (clockLoading) return;
      setClockLoading(true);
      try {
        if (activeClock?.job === job.id) {
          await clockOut(job.id);
          setActiveClock(null);
        } else {
          if (activeClock?.job) await clockOut(activeClock.job);
          const options = { laborType };
          if (customStartTime) options.customStartTime = customStartTime;
          const result = await clockIn(job.id, options);
          setActiveClock({ job: job.id, clock_in_at: result?.clock_in_at, ...result });
        }
        refresh();
      } catch (err) {
        alert(err?.response?.data?.error || 'Clock action failed');
      } finally {
        setClockLoading(false);
      }
    },
    [activeClock, clockLoading, refresh],
  );

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      {/* ── Page-level tabs ── */}
      <div className="flex items-center gap-0 border-b border-gray-200 mb-4">
        <PageTab label="Active Queue" badge={jobs.length} active={pageTab === 'queue'} onClick={() => setPageTab('queue')} color="blue" />
        <PageTab label="Ready to Ship" badge={readyToShipJobs.length} active={pageTab === 'ready_to_ship'} onClick={() => setPageTab('ready_to_ship')} color="orange" />
        <PageTab label="Shipped" badge={shippedJobs.length} active={pageTab === 'shipped'} onClick={() => setPageTab('shipped')} color="green" />
        <PageTab label="Archived" active={pageTab === 'archived'} onClick={() => setPageTab('archived')} color="gray" />
        <PageTab label="Cancelled" active={pageTab === 'cancelled'} onClick={() => setPageTab('cancelled')} color="red" />
        <PageTab label="Purchasing" badge={purchasingBadge} active={pageTab === 'purchasing'} onClick={() => setPageTab('purchasing')} color="violet" />
      </div>

      {pageTab === 'ready_to_ship' && (
        <ReadyToShipView jobs={readyToShipJobs} loading={readyToShipLoading} onRefresh={loadReadyToShipJobs} />
      )}
      {pageTab === 'shipped' && (
        <ShippedView jobs={shippedJobs} loading={shippedLoading} onRefresh={loadShippedJobs} />
      )}
      {pageTab === 'archived' && (
        <ArchivedView jobs={archivedJobs} loading={archivedLoading} onRefresh={loadArchivedJobs} />
      )}
      {pageTab === 'cancelled' && (
        <CancelledView jobs={cancelledJobs} loading={cancelledLoading} onRefresh={loadCancelledJobs} />
      )}
      {pageTab === 'purchasing' && (
        <PurchasingTab />
      )}

      {/* ── Active Queue ── */}
      {pageTab === 'queue' && (
        <div>
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-foreground">Priority Queue</h1>
            <div className="flex items-center gap-3">
              <button
                onClick={async () => {
                  setSuggestLoading(true);
                  try {
                    const result = await handleApplySuggested();
                    if (result) {
                      setAiReasoningResult(result);
                      setAiModalOpen(true);
                    }
                  }
                  catch { /* ignore */ }
                  finally { setSuggestLoading(false); }
                }}
                disabled={suggestLoading}
                title="AI analyzes queue and optimizes for maximum throughput"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-violet-300 text-violet-700 bg-violet-50 hover:bg-violet-100 disabled:opacity-50 transition-colors"
              >
                <svg className={`w-3.5 h-3.5 ${suggestLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                {suggestLoading ? 'AI Analyzing…' : 'AI Optimize'}
              </button>
              <button
                onClick={() => { setReportAutoGen(!hasNewReport); setReportOpen(true); setHasNewReport(false); }}
                className="relative flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                AI Report
                {hasNewReport && (
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
                )}
              </button>
              <Button size="sm" onClick={() => setCreateOpen(true)}>+ New Job</Button>
              <button
                onClick={refresh}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
                title="Refresh"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
          </div>

          {/* Filter bar */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {filterTabs.map((f) => (
              <button
                key={f.key}
                onClick={() => setActiveFilter(f.key)}
                className={`flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                  activeFilter === f.key
                    ? ACCENT_ACTIVE[f.accent]
                    : ACCENT_INACTIVE[f.accent]
                }`}
              >
                {f.label}
                {f.count > 0 && (
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center ${
                    activeFilter === f.key
                      ? 'bg-white/25 text-inherit'
                      : 'bg-gray-200/60 text-gray-600'
                  }`}>
                    {f.count}
                  </span>
                )}
              </button>
            ))}
            <div className="ml-auto">
              <SearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Filter jobs..."
                className="w-48"
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded mb-4">
              {error}
            </div>
          )}

          {filteredJobs.length === 0 ? (
            <EmptyState
              title={activeFilter !== 'all' || searchQuery ? 'No matching jobs' : 'Queue is empty'}
              description={
                activeFilter !== 'all' || searchQuery
                  ? 'Try changing filters or clearing search.'
                  : 'No jobs are currently in the queue.'
              }
            />
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToVerticalAxis]}
            >
              <SortableContext
                items={filteredJobs.map((j) => j.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2.5">
                  {filteredJobs.map((job) => (
                    <SortableJobCard
                      key={job.id}
                      job={job}
                      onClockToggle={handleClockToggle}
                      onPinToggle={handlePin}
                      activeClock={activeClock}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}

          <JobCreateModal open={createOpen} onClose={() => { setCreateOpen(false); refresh(); }} />
          <DailyReportModal
            open={reportOpen}
            onClose={() => { setReportOpen(false); setReportAutoGen(false); }}
            autoGenerate={reportAutoGen}
          />
          <AIReasoningModal
            open={aiModalOpen}
            onClose={() => setAiModalOpen(false)}
            result={aiReasoningResult}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function PageTab({ label, badge, active, onClick, color }) {
  const colorMap = {
    blue:   active ? 'border-blue-600 text-blue-600'     : 'border-transparent text-gray-500 hover:text-gray-700',
    orange: active ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700',
    green:  active ? 'border-green-600 text-green-700'   : 'border-transparent text-gray-500 hover:text-gray-700',
    gray:   active ? 'border-gray-500 text-gray-700'     : 'border-transparent text-gray-500 hover:text-gray-700',
    red:    active ? 'border-red-500 text-red-600'       : 'border-transparent text-gray-500 hover:text-gray-700',
    violet: active ? 'border-violet-600 text-violet-600' : 'border-transparent text-gray-500 hover:text-gray-700',
  };
  const badgeColorMap = {
    blue:   'bg-blue-100 text-blue-700',
    orange: 'bg-orange-100 text-orange-700',
    green:  'bg-green-100 text-green-700',
    gray:   'bg-gray-200 text-gray-600',
    red:    'bg-red-100 text-red-700',
    violet: 'bg-violet-100 text-violet-700',
  };

  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${colorMap[color]}`}
    >
      {label}
      {badge != null && badge > 0 && (
        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center ${
          active ? badgeColorMap[color] : 'bg-gray-100 text-gray-500'
        }`}>
          {badge}
        </span>
      )}
    </button>
  );
}

function RefreshBtn({ onClick }) {
  return (
    <button onClick={onClick} className="p-1.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100" title="Refresh">
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    </button>
  );
}

function JobTable({ jobs, columns }) {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
            {columns.map((c) => <th key={c.key} className="px-4 py-2">{c.label}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {jobs.map((job) => (
            <tr key={job.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => window.location.href = `/jobs/${job.id}`}>
              {columns.map((c) => <td key={c.key} className="px-4 py-2">{c.render(job)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReadyToShipView({ jobs, loading, onRefresh }) {
  const cols = [
    { key: 'job', label: 'Job #', render: (j) => <span className="font-mono text-xs font-bold text-gray-800">{j.job_number}</span> },
    { key: 'cust', label: 'Customer', render: (j) => <span className="text-gray-700">{j.customer_name || '—'}</span> },
    { key: 'item', label: 'Parent Item', render: (j) => <span className="text-xs font-semibold bg-gray-100 px-1.5 py-0.5 rounded">{j.parent_item_no || '—'}</span> },
    { key: 'qty', label: 'Qty', render: (j) => <span className="font-medium">{j.quantity || 1}</span> },
    { key: 'completed', label: 'Completed', render: (j) => <span className="text-xs text-gray-500">{j.completion_date ? format(new Date(j.completion_date + 'T00:00:00'), 'MMM d, yyyy') : '—'}</span> },
    { key: 'due', label: 'Due Date', render: (j) => <span className="text-xs text-gray-500">{j.target_due_date ? format(new Date(j.target_due_date + 'T00:00:00'), 'MMM d, yyyy') : '—'}</span> },
  ];
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-gray-700">📦 Ready to Ship <span className="text-sm font-normal text-gray-400">({jobs.length})</span></h2>
        <RefreshBtn onClick={onRefresh} />
      </div>
      {loading ? <div className="flex justify-center py-10"><LoadingSpinner /></div>
      : jobs.length === 0 ? <EmptyState title="No jobs ready to ship" description="Jobs marked complete will appear here until shipped." />
      : <JobTable jobs={jobs} columns={cols} />}
    </div>
  );
}

function ShippedView({ jobs, loading, onRefresh }) {
  const cols = [
    { key: 'job', label: 'Job #', render: (j) => <span className="font-mono text-xs font-bold text-gray-800">{j.job_number}</span> },
    { key: 'cust', label: 'Customer', render: (j) => <span className="text-gray-700">{j.customer_name || '—'}</span> },
    { key: 'item', label: 'Parent Item', render: (j) => <span className="text-xs font-semibold bg-gray-100 px-1.5 py-0.5 rounded">{j.parent_item_no || '—'}</span> },
    { key: 'status', label: 'Status', render: () => <span className="px-2 py-0.5 rounded text-xs font-medium bg-sky-100 text-sky-700">Shipped</span> },
    { key: 'shipped', label: 'Shipped', render: (j) => <span className="text-xs text-gray-500">{j.shipped_at ? format(new Date(j.shipped_at), 'MMM d, yyyy') : '—'}</span> },
    { key: 'invoice', label: 'Invoice #', render: (j) => <span className="text-xs text-gray-500">{j.invoice_number || <span className="text-gray-300 italic">None</span>}</span> },
  ];
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-gray-700">🚚 Shipped <span className="text-sm font-normal text-gray-400">({jobs.length})</span></h2>
        <RefreshBtn onClick={onRefresh} />
      </div>
      {loading ? <div className="flex justify-center py-10"><LoadingSpinner /></div>
      : jobs.length === 0 ? <EmptyState title="No shipped jobs" description="Ship a completed job and it will appear here until archived." />
      : <JobTable jobs={jobs} columns={cols} />}
    </div>
  );
}

function ArchivedView({ jobs, loading, onRefresh }) {
  const cols = [
    { key: 'job', label: 'Job #', render: (j) => <span className="font-mono text-xs font-bold text-gray-800">{j.job_number}</span> },
    { key: 'cust', label: 'Customer', render: (j) => <span className="text-gray-700">{j.customer_name || '—'}</span> },
    { key: 'item', label: 'Parent Item', render: (j) => <span className="text-xs font-semibold bg-gray-100 px-1.5 py-0.5 rounded">{j.parent_item_no || '—'}</span> },
    { key: 'shipped', label: 'Shipped', render: (j) => <span className="text-xs text-gray-500">{j.shipped_at ? format(new Date(j.shipped_at), 'MMM d, yyyy') : '—'}</span> },
    { key: 'archived', label: 'Archived', render: (j) => <span className="text-xs text-gray-500">{j.archived_at ? format(new Date(j.archived_at), 'MMM d, yyyy') : '—'}</span> },
    { key: 'invoice', label: 'Invoice #', render: (j) => <span className="text-xs text-gray-500">{j.invoice_number || '—'}</span> },
    { key: 'cost', label: 'True Cost', render: (j) => <span className="text-xs font-medium text-gray-700">{j.true_cost ? `$${parseFloat(j.true_cost).toLocaleString()}` : '—'}</span> },
  ];
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-gray-700">🗄️ Archived <span className="text-sm font-normal text-gray-400">({jobs.length})</span></h2>
        <RefreshBtn onClick={onRefresh} />
      </div>
      {loading ? <div className="flex justify-center py-10"><LoadingSpinner /></div>
      : jobs.length === 0 ? <EmptyState title="No archived jobs" description="Shipped jobs move here after archiving with invoice and cost info." />
      : <JobTable jobs={jobs} columns={cols} />}
    </div>
  );
}

function CancelledView({ jobs, loading, onRefresh }) {
  const cols = [
    { key: 'job', label: 'Job #', render: (j) => <span className="font-mono text-xs font-bold text-gray-800">{j.job_number}</span> },
    { key: 'cust', label: 'Customer', render: (j) => <span className="text-gray-700">{j.customer_name || '—'}</span> },
    { key: 'item', label: 'Parent Item', render: (j) => <span className="text-xs font-semibold bg-gray-100 px-1.5 py-0.5 rounded">{j.parent_item_no || '—'}</span> },
    { key: 'status', label: 'Status', render: () => <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">Cancelled</span> },
    { key: 'cancelled', label: 'Cancelled', render: (j) => <span className="text-xs text-gray-500">{j.cancelled_at ? format(new Date(j.cancelled_at), 'MMM d, yyyy') : '—'}</span> },
    { key: 'reason', label: 'Reason', render: (j) => <span className="text-xs text-gray-500 max-w-[200px] truncate block">{j.cancelled_reason || <span className="text-gray-300 italic">No reason</span>}</span> },
  ];
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-gray-700">❌ Cancelled <span className="text-sm font-normal text-gray-400">({jobs.length})</span></h2>
        <RefreshBtn onClick={onRefresh} />
      </div>
      {loading ? <div className="flex justify-center py-10"><LoadingSpinner /></div>
      : jobs.length === 0 ? <EmptyState title="No cancelled jobs" description="Jobs you cancel will appear here." />
      : <JobTable jobs={jobs} columns={cols} />}
    </div>
  );
}
